import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy, 
  onSnapshot, 
  where,
  Timestamp,
  getDocs
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from './useNotifications';
import { Project, CreateProjectData, ProjectStats } from '../types/project';
import { Task } from '../types/task';

export const useProjects = () => {
  const { user } = useAuth();
  const { createNotification, settings } = useNotifications();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  // Helper függvény: undefined értékek kiszűrése a notification data-ból
  const cleanNotificationData = (data: any) => {
    const cleaned: any = {};
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        cleaned[key] = data[key];
      }
    });
    return cleaned;
  };

  // Projektek lekérése
  useEffect(() => {
    if (!user?.organizationId) {
      setLoading(false);
      return;
    }

    const projectsQuery = query(
      collection(db, 'projects'),
      where('organizationId', '==', user.organizationId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(projectsQuery, (snapshot) => {
      const projectsData = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          startDate: data.startDate?.toDate && !isNaN(data.startDate.toDate().getTime()) ? data.startDate.toDate() : new Date(),
          endDate: data.endDate?.toDate && !isNaN(data.endDate.toDate().getTime()) ? data.endDate.toDate() : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          documents: data.documents || []
        } as Project;
      });
      setProjects(projectsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user?.organizationId]);

  // Projekt létrehozása
  const createProject = useCallback(async (projectData: CreateProjectData) => {
    if (!user?.organizationId) throw new Error('No organization ID');

    const newProject = {
      ...projectData,
      startDate: Timestamp.fromDate(projectData.startDate),
      endDate: Timestamp.fromDate(projectData.endDate),
      taskIds: [],
      progress: 0,
      documents: projectData.documents || [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      createdBy: user.uid,
      createdByName: user.displayName || undefined,
      organizationId: user.organizationId,
      isActive: true
    };

    const docRef = await addDoc(collection(db, 'projects'), newProject);

    // Értesítés küldése a projekt létrehozásáról
    try {
      // Ha van csapat tag, értesítjük őket
      if (projectData.assignedUsers && projectData.assignedUsers.length > 0) {
        for (const memberId of projectData.assignedUsers) {
          if (memberId !== user.uid || settings?.debugMode) {
            await createNotification({
              type: 'project_update',
              title: 'Új projekt létrehozva',
              message: `${projectData.name}: ${projectData.description}`,
              data: {
                projectId: docRef.id,
                projectName: projectData.name
              },
              priority: projectData.priority || 'medium',
              userId: memberId,
              organizationId: user.organizationId,
              createdBy: user.uid,
              createdByName: user.displayName,
              actionUrl: `/projects?project=${docRef.id}`
            });
          }
        }
      }
    } catch (notificationError) {
      console.error('Error sending project creation notification:', notificationError);
      // Ne akadályozzuk meg a projekt létrehozását, ha az értesítés sikertelen
    }
  }, [user]);

  // Projekt frissítése
  const updateProject = useCallback(async (projectId: string, updates: Partial<CreateProjectData>) => {
    if (!user?.organizationId) throw new Error('No organization ID');

    const updateData: any = {
      ...updates,
      updatedAt: Timestamp.now()
    };

    // Dátumok konvertálása ha szükséges
    if (updates.startDate) {
      updateData.startDate = Timestamp.fromDate(updates.startDate);
    }
    if (updates.endDate) {
      updateData.endDate = Timestamp.fromDate(updates.endDate);
    }

    await updateDoc(doc(db, 'projects', projectId), updateData);

    // Értesítés küldése a projekt frissítéséről
    try {
      const currentProject = projects.find(p => p.id === projectId);
      if (currentProject && currentProject.assignedUsers.length > 0) {
        
        // Specifikus értesítések státusz és prioritás változásokhoz
        const hasStatusChange = updates.status !== undefined && updates.status !== currentProject.status;
        const hasPriorityChange = updates.priority !== undefined && updates.priority !== currentProject.priority;
        
        for (const memberId of currentProject.assignedUsers) {
          if (memberId !== user.uid || settings?.debugMode) {
            
            // Státusz változás értesítés
            if (hasStatusChange) {
              await createNotification({
                type: 'project_update',
                title: 'Projekt státusz változott',
                message: `${currentProject.name} projekt státusza: ${updates.status}`,
                data: cleanNotificationData({
                  projectId: projectId,
                  projectName: currentProject.name,
                  newStatus: updates.status,
                  oldStatus: currentProject.status
                }),
                priority: currentProject.priority || 'medium',
                userId: memberId,
                organizationId: user.organizationId,
                createdBy: user.uid,
                createdByName: user.displayName,
                actionUrl: `/projects?project=${projectId}`
              });
            }
            
            // Prioritás változás értesítés
            if (hasPriorityChange) {
              await createNotification({
                type: 'project_update',
                title: 'Projekt prioritás változott',
                message: `${currentProject.name} projekt prioritása: ${updates.priority}`,
                data: cleanNotificationData({
                  projectId: projectId,
                  projectName: currentProject.name,
                  newPriority: updates.priority,
                  oldPriority: currentProject.priority
                }),
                priority: updates.priority || 'medium',
                userId: memberId,
                organizationId: user.organizationId,
                createdBy: user.uid,
                createdByName: user.displayName,
                actionUrl: `/projects?project=${projectId}`
              });
            }
            
            // Általános frissítés értesítés, ha nem csak státusz vagy prioritás változott
            if (!hasStatusChange && !hasPriorityChange) {
              await createNotification({
                type: 'project_update',
                title: 'Projekt frissítve',
                message: `${currentProject.name} projekt frissült`,
                data: {
                  projectId: projectId,
                  projectName: currentProject.name
                },
                priority: currentProject.priority || 'medium',
                userId: memberId,
                organizationId: user.organizationId,
                createdBy: user.uid,
                createdByName: user.displayName,
                actionUrl: `/projects?project=${projectId}`
              });
            }
          }
        }
      }
    } catch (notificationError) {
      console.error('Error sending project update notification:', notificationError);
      // Ne akadályozzuk meg a projekt frissítést, ha az értesítés sikertelen
    }
  }, [user, projects, createNotification, cleanNotificationData]);

  // Projekt törlése
  const deleteProject = useCallback(async (projectId: string) => {
    if (!user?.organizationId) throw new Error('No organization ID');

    await deleteDoc(doc(db, 'projects', projectId));
  }, [user]);

  // Projekt státusz frissítése
  const updateProjectStatus = useCallback(async (projectId: string, status: Project['status']) => {
    if (!user?.organizationId) throw new Error('No organization ID');

    await updateDoc(doc(db, 'projects', projectId), {
      status,
      updatedAt: Timestamp.now()
    });

    // Értesítés küldése a projekt státusz változásról
    try {
      const currentProject = projects.find(p => p.id === projectId);
      if (currentProject && currentProject.assignedUsers.length > 0) {
        for (const memberId of currentProject.assignedUsers) {
          if (memberId !== user.uid || settings?.debugMode) {
            await createNotification({
              type: 'project_update',
              title: 'Projekt státusz változott',
              message: `${currentProject.name} projekt státusza: ${status}`,
              data: cleanNotificationData({
                projectId: projectId,
                projectName: currentProject.name,
                newStatus: status,
                oldStatus: currentProject.status
              }),
              priority: currentProject.priority || 'medium',
              userId: memberId,
              organizationId: user.organizationId,
              createdBy: user.uid,
              createdByName: user.displayName,
              actionUrl: `/projects?project=${projectId}`
            });
          }
        }
      }
    } catch (notificationError) {
      console.error('Error sending project status notification:', notificationError);
      // Ne akadályozzuk meg a státusz frissítést, ha az értesítés sikertelen
    }
  }, [user, projects, createNotification]);

  // Projekt prioritás frissítése
  const updateProjectPriority = useCallback(async (projectId: string, priority: Project['priority']) => {
    if (!user?.organizationId) throw new Error('No organization ID');

    await updateDoc(doc(db, 'projects', projectId), {
      priority,
      updatedAt: Timestamp.now()
    });

    // Értesítés küldése a projekt prioritás változásról
    try {
      const currentProject = projects.find(p => p.id === projectId);
      if (currentProject && currentProject.assignedUsers.length > 0) {
        for (const memberId of currentProject.assignedUsers) {
          if (memberId !== user.uid || settings?.debugMode) {
            await createNotification({
              type: 'project_update',
              title: 'Projekt prioritás változott',
              message: `${currentProject.name} projekt prioritása: ${priority}`,
              data: cleanNotificationData({
                projectId: projectId,
                projectName: currentProject.name,
                newPriority: priority,
                oldPriority: currentProject.priority
              }),
              priority: priority,
              userId: memberId,
              organizationId: user.organizationId,
              createdBy: user.uid,
              createdByName: user.displayName,
              actionUrl: `/projects?project=${projectId}`
            });
          }
        }
      }
    } catch (notificationError) {
      console.error('Error sending project priority notification:', notificationError);
      // Ne akadályozzuk meg a prioritás frissítést, ha az értesítés sikertelen
    }
  }, [user, projects, createNotification]);

  // Projekt progressz frissítése
  const updateProjectProgress = useCallback(async (projectId: string, progress: number) => {
    if (!user?.organizationId) throw new Error('No organization ID');

    const clampedProgress = Math.max(0, Math.min(100, progress));

    await updateDoc(doc(db, 'projects', projectId), {
      progress: clampedProgress,
      updatedAt: Timestamp.now()
    });

    // Értesítés küldése, ha a projekt 100%-ot ért el
    try {
      const currentProject = projects.find(p => p.id === projectId);
      if (currentProject && clampedProgress === 100 && currentProject.progress < 100) {
        if (currentProject.assignedUsers.length > 0) {
          for (const memberId of currentProject.assignedUsers) {
            if (memberId !== user.uid || settings?.debugMode) {
              await createNotification({
                type: 'project_update',
                title: 'Projekt befejezve',
                message: `${currentProject.name} projekt elérte a 100%-ot!`,
                data: cleanNotificationData({
                  projectId: projectId,
                  projectName: currentProject.name,
                  newProgress: clampedProgress,
                  oldProgress: currentProject.progress
                }),
                priority: 'high',
                userId: memberId,
                organizationId: user.organizationId,
                createdBy: user.uid,
                createdByName: user.displayName,
                actionUrl: `/projects?project=${projectId}`
              });
            }
          }
        }
      }
    } catch (notificationError) {
      console.error('Error sending project progress notification:', notificationError);
      // Ne akadályozzuk meg a progressz frissítést, ha az értesítés sikertelen
    }
  }, [user, projects, createNotification]);

  // Feladat hozzáadása projekthez
  const addTaskToProject = useCallback(async (projectId: string, taskId: string) => {
    if (!user?.organizationId) throw new Error('No organization ID');

    const project = projects.find(p => p.id === projectId);
    if (!project) throw new Error('Project not found');

    const updatedTaskIds = [...project.taskIds, taskId];
    await updateDoc(doc(db, 'projects', projectId), {
      taskIds: updatedTaskIds,
      updatedAt: Timestamp.now()
    });
  }, [projects, user]);

  // Feladat eltávolítása projektből
  const removeTaskFromProject = useCallback(async (projectId: string, taskId: string) => {
    if (!user?.organizationId) throw new Error('No organization ID');

    const project = projects.find(p => p.id === projectId);
    if (!project) throw new Error('Project not found');

    const updatedTaskIds = project.taskIds.filter(id => id !== taskId);
    await updateDoc(doc(db, 'projects', projectId), {
      taskIds: updatedTaskIds,
      updatedAt: Timestamp.now()
    });
  }, [projects, user]);

  // Projekt automatikus progressz számítása feladatok alapján
  const calculateProjectProgress = useCallback(async (projectId: string) => {
    if (!user?.organizationId) throw new Error('No organization ID');

    const project = projects.find(p => p.id === projectId);
    if (!project || project.taskIds.length === 0) return;

    // Feladatok lekérése
    const tasksQuery = query(
      collection(db, 'tasks'),
      where('__name__', 'in', project.taskIds)
    );

    const tasksSnapshot = await getDocs(tasksQuery);
    const tasks = tasksSnapshot.docs.map(doc => doc.data()) as Task[];

    if (tasks.length === 0) return;

    // Progressz számítása
    const completedTasks = tasks.filter(task => task.status === 'completed').length;
    const progress = Math.round((completedTasks / tasks.length) * 100);

    await updateProjectProgress(projectId, progress);
  }, [projects, user, updateProjectProgress]);

  // Projekt statisztikák
  const getProjectStats = useCallback((): ProjectStats => {
    const stats: ProjectStats = {
      total: projects.length,
      planning: 0,
      active: 0,
      completed: 0,
      paused: 0,
      cancelled: 0,
      byPriority: {
        low: 0,
        medium: 0,
        high: 0,
        urgent: 0
      },
      averageProgress: 0,
      overdueCount: 0
    };

    if (projects.length === 0) return stats;

    let totalProgress = 0;
    const now = new Date();

    projects.forEach(project => {
      // Státusz számolás
      stats[project.status]++;
      
      // Prioritás számolás
      stats.byPriority[project.priority]++;
      
      // Progressz összesítés
      totalProgress += project.progress;
      
      // Lejárt projektek
      if (project.endDate < now && project.status !== 'completed') {
        stats.overdueCount++;
      }
    });

    stats.averageProgress = Math.round(totalProgress / projects.length);

    return stats;
  }, [projects]);

  // Saját projektek lekérése
  const getMyProjects = useCallback(() => {
    if (!user?.uid) return [];
    
    return projects.filter(project => 
      project.assignedUsers.includes(user.uid) ||
      project.createdBy === user.uid
    );
  }, [projects, user]);

  // Aktív projektek lekérése
  const getActiveProjects = useCallback(() => {
    return projects.filter(project => project.status === 'active');
  }, [projects]);

  // Lejárt projektek lekérése
  const getOverdueProjects = useCallback(() => {
    const now = new Date();
    return projects.filter(project => 
      project.endDate < now && 
      project.status !== 'completed' && 
      project.status !== 'cancelled'
    );
  }, [projects]);

  return {
    projects,
    loading,
    createProject,
    updateProject,
    deleteProject,
    updateProjectStatus,
    updateProjectPriority,
    updateProjectProgress,
    addTaskToProject,
    removeTaskFromProject,
    calculateProjectProgress,
    getProjectStats,
    getMyProjects,
    getActiveProjects,
    getOverdueProjects
  };
}; 